import { useState, useEffect } from "react";
import { IonPage, IonContent, IonAlert } from "@ionic/react";
import { Check, Copy, DoorOpen, Link2, UserMinus } from "lucide-react";
import { PageHeader } from "../../components/ui/PageHeader";
import { Avatar } from "../../components/ui/Avatar";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { IconButton } from "../../components/ui/IconButton";
import { SkeletonList } from "../../components/ui/Skeleton";
import { useHistory } from "react-router-dom";
import { useStore } from "../../store/useStore";
import { useToast } from "../../hooks/useToast";
import type { IHouseholdMemberInfo } from "../../types";
import { getRoleBadge } from "../../logic/households";

type IMemberInfo = IHouseholdMemberInfo;


export default function MembersPage() {
  const history = useHistory();
  const user = useStore((s) => s.user);
  const currentHouseholdId = useStore((s) => s.currentHouseholdId);
  const households = useStore((s) => s.households);
  const userHouseholds = useStore((s) => s.userHouseholds);
  const updateMemberRole = useStore((s) => s.updateMemberRole);
  const removeMember = useStore((s) => s.removeMember);
  const leaveHousehold = useStore((s) => s.leaveHousehold);
  const fetchHouseholdMembers = useStore((s) => s.fetchHouseholdMembers);
  const showToast = useToast((s) => s.showToast);

  const [members, setMembers] = useState<IMemberInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);

  const currentHousehold = households.find((h) => h.uuid === currentHouseholdId);
  const currentUserHousehold = userHouseholds.find(
    (uh) => uh.household_uuid === currentHouseholdId && uh.user_uuid === user?.id
  );
  const isParent = currentUserHousehold?.role === "parent";
  const ownerUuid = currentHousehold?.created_by_uuid ?? null;
  const isOwner = user?.id != null && user.id === ownerUuid;
  const parentCount = members.filter((m) => m.role === "parent").length;

  useEffect(() => {
    if (!currentHouseholdId) return;

    const fetchMembers = async () => {
      setIsLoading(true);
      const list = await fetchHouseholdMembers(currentHouseholdId);
      setMembers(list);
      setIsLoading(false);
    };

    fetchMembers();
  }, [currentHouseholdId, userHouseholds, fetchHouseholdMembers]);

  const handleRoleChange = async (membershipId: string, newRole: string) => {
    if (newRole !== "parent" && newRole !== "child" && newRole !== "cleaner") return;
    try {
      await updateMemberRole(membershipId, newRole);
      setMembers((prev) =>
        prev.map((m) =>
          m.membership_uuid === membershipId ? { ...m, role: newRole } : m
        )
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update role";
      showToast(message, "danger");
    }
  };

  const handleRemove = async (membershipId: string) => {
    try {
      await removeMember(membershipId);
      setMembers((prev) => prev.filter((m) => m.membership_uuid !== membershipId));
      setConfirmRemove(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to remove member";
      showToast(message, "danger");
      setConfirmRemove(null);
    }
  };

  const handleLeave = async () => {
    if (!currentHouseholdId) return;
    setIsLeaving(true);
    try {
      const hasRemaining = await leaveHousehold(currentHouseholdId);
      setConfirmLeave(false);
      if (hasRemaining) {
        history.push("/today");
      } else {
        history.push("/onboarding");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to leave household";
      showToast(message, "danger");
      setIsLeaving(false);
      setConfirmLeave(false);
    }
  };

  const handleGenerateInvite = () => {
    if (!currentHousehold) return;
    const link = `${window.location.origin}/invite?code=${currentHousehold.invite_code}`;
    setInviteLink(link);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopySuccess(true);
      showToast("Invite link copied!", "success");
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = inviteLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopySuccess(true);
      showToast("Invite link copied!", "success");
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const isMemberOwner = (memberUserUuid: string) =>
    ownerUuid != null && memberUserUuid === ownerUuid;

  const canChangeRole = (member: IMemberInfo) => {
    if (!isParent) return false;
    const isSelf = member.user_uuid === user?.id;
    if (isMemberOwner(member.user_uuid)) return false;
    // Sole parent cannot demote themselves
    if (isSelf && member.role === "parent" && parentCount <= 1) return false;
    if (isSelf) return false;
    return true;
  };

  const canRemove = (member: IMemberInfo) => {
    if (!isParent) return false;
    if (member.user_uuid === user?.id) return false;
    if (isMemberOwner(member.user_uuid)) return false;
    return true;
  };

  return (
    <IonPage>
      <PageHeader title="Members" backHref="/settings" />
      <IonContent>
        <div className="max-w-lg mx-auto px-4 py-4 pb-28">
          {isLoading ? (
            <SkeletonList rows={3} />
          ) : (
            <>
              {/* Invite section — parents only */}
              {isParent && currentHousehold && (
                <div className="card-surface p-4 mb-6">
                  <h3 className="font-bold text-foreground mb-1">
                    Invite Member
                  </h3>
                  <p className="text-muted-foreground text-sm mb-3">
                    Share this link to invite someone to your household.
                  </p>

                  {inviteLink ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={inviteLink}
                        readOnly
                        aria-label="Invite link"
                        className="input-field text-sm"
                        onFocus={(e) => e.currentTarget.select()}
                      />
                      <Button
                        onClick={handleCopyLink}
                        variant={copySuccess ? "soft" : "primary"}
                        leadingIcon={
                          copySuccess ? (
                            <Check className="w-4 h-4" aria-hidden="true" />
                          ) : (
                            <Copy className="w-4 h-4" aria-hidden="true" />
                          )
                        }
                        className="min-h-[48px]"
                      >
                        {copySuccess ? "Copied!" : "Copy"}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="soft"
                      onClick={handleGenerateInvite}
                      leadingIcon={<Link2 className="w-4 h-4" aria-hidden="true" />}
                    >
                      Generate Invite Link
                    </Button>
                  )}
                </div>
              )}

              <h2 className="px-1 mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Members ({members.length})
              </h2>

              <ul className="card-surface overflow-hidden divide-y divide-border">
                {members.map((member) => {
                  const isSelf = member.user_uuid === user?.id;
                  const memberIsOwner = isMemberOwner(member.user_uuid);

                  return (
                    <li
                      key={member.membership_uuid}
                      className="flex items-center gap-3 px-4 py-3 min-h-[64px]"
                    >
                      <Avatar
                        name={member.name}
                        seed={member.user_uuid}
                        photoUrl={member.profile_photo_url}
                      />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-bold text-foreground truncate">
                            {member.name}
                            {isSelf && (
                              <span className="font-medium text-muted-foreground"> (you)</span>
                            )}
                          </span>
                          {memberIsOwner && <Badge tone="solid">Owner</Badge>}
                        </div>

                        {canChangeRole(member) ? (
                          <select
                            value={member.role}
                            aria-label={`Role for ${member.name}`}
                            onChange={(e) =>
                              handleRoleChange(member.membership_uuid, e.target.value)
                            }
                            className="mt-1 min-h-[36px] text-sm font-semibold px-3 pr-8 rounded-xl bg-surface border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          >
                            <option value="parent">Parent</option>
                            <option value="child">Child</option>
                            <option value="cleaner">Cleaner</option>
                          </select>
                        ) : (
                          <Badge tone={getRoleBadge(member.role, false).tone} className="mt-1">
                            {getRoleBadge(member.role, false).label}
                          </Badge>
                        )}
                      </div>

                      {canRemove(member) && (
                        <IconButton
                          label={`Remove ${member.name}`}
                          icon={<UserMinus className="w-5 h-5" />}
                          onClick={() => setConfirmRemove(member.membership_uuid)}
                          className="text-destructive hover:bg-destructive/10"
                        />
                      )}
                    </li>
                  );
                })}
              </ul>

              {/* Leave Household — non-owners only */}
              {!isOwner && (
                <div className="mt-8">
                  <Button
                    variant="danger"
                    block
                    leadingIcon={<DoorOpen className="w-4 h-4" aria-hidden="true" />}
                    onClick={() => setConfirmLeave(true)}
                    loading={isLeaving}
                  >
                    {isLeaving ? "Leaving…" : "Leave Household"}
                  </Button>
                </div>
              )}

              {/* Remove member confirmation */}
              <IonAlert
                isOpen={confirmRemove !== null}
                header="Remove Member?"
                message="This person will lose access to the household. They can rejoin using the invite code."
                onDidDismiss={() => setConfirmRemove(null)}
                buttons={[
                  { text: "Cancel", role: "cancel" },
                  {
                    text: "Remove",
                    role: "destructive",
                    handler: () => {
                      if (confirmRemove) handleRemove(confirmRemove);
                    },
                  },
                ]}
              />

              {/* Leave household confirmation */}
              <IonAlert
                isOpen={confirmLeave}
                header="Leave Household?"
                message="You'll lose access to all tasks and data in this household."
                onDidDismiss={() => setConfirmLeave(false)}
                buttons={[
                  { text: "Cancel", role: "cancel" },
                  {
                    text: "Leave",
                    role: "destructive",
                    handler: () => {
                      handleLeave();
                    },
                  },
                ]}
              />
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
